import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Box, Divider } from "@mui/material";
import { useDispatch } from "react-redux";
import { styled } from "@mui/material/styles";
import { Tab, Tabs } from "@mui/material";
import useMediaQuery from "@mui/material/useMediaQuery";
import LibraryTable from "../../components/CommentLibrary/LibraryTable";
import CLSummary from "../../components/CommentLibrary/CommentLibraryTags/CLSummary";
import { useSelector } from "react-redux";
import { apiCall } from "../../utils/action";
import ImportProgress from "../../components/CommentLibrary/ImportProgress";
import CLRating from "./CLRating";
import GlobalList from "../../components/CommentLibrary/CommentLibraryTags/GlobalList";
import Header from "../../common/header/Header";
import Breadcrumb from "../../common/header/Breadcrumb";
import CLTagsTable from "./CLTagsTable";

const StyledTabs = styled((props) => <Tabs {...props} TabIndicatorProps={{ children: <span className="MuiTabs-indicatorSpan" /> }} />)({
    "& .MuiTabs-indicator": {
        display: "flex",
        justifyContent: "center",
        backgroundColor: "transparent",
    },
    "& .MuiTabs-indicatorSpan": {
        maxWidth: 75,
        borderRadius: 5,
        width: "100%",
        height: "30px !important",
        backgroundColor: "#1F9C7B",
    },
});

const StyledTab = styled((props) => (
    <Tab
        style={{
            width: props?.width,
            textTransform: "none",
            fontWeight: "500",
            fontFamily: '"Source Sans Pro", sans-serif !important',
            fontStyle: "normal",
            fontSize: "14px",
            lineHeight: "20px",
            letterSpacing: "0.1px",
            marginRight: "8px",
            color: "#42474E",
        }}
        disableRipple
        {...props}
    ></Tab>
))(({ theme }) => ({
    textTransform: "none",
    fontWeight: "500 !important",
    fontFamily: '"Source Sans Pro", sans-serif !important',
    fontStyle: "normal !important",
    fontSize: theme.typography.pxToRem(14),
    lineHeight: "20px !important",
    letterSpacing: "0.1px !important",
    marginRight: theme.spacing(1),
    color: "#3D454D !important",
    "&.Mui-selected": {
        color: "##3D454D !important",
    },
    "&.Mui-focusVisible": {
        backgroundColor: "#1F9C7B !important",
    },
}));

const STATUS_MAP = {
    all: undefined,
    used: 'used',
    unused: 'unused',
    summary: 'summary',
    deleted: 'deleted',
};

function CommentLibrary() {
    const [tabValue, setTabValue] = useState(0);
    const [searchParams] = useSearchParams();
    const matches = useMediaQuery("(min-width:900px)");
    const globalListStoreData = useSelector((state) => state.globalList);
    const storeData = useSelector((state) => state.commentLibrary);
    const [importing, setImporting] = useState(false);
    const [progress, setProgress] = useState(0);
    const [state, setState] = useState({
        page: 0,
        rowsPerPage: parseInt(sessionStorage.getItem("commentsRowPerPage"), 10) || 10,
        order: "",
        orderBy: "",
        search: false,
        selected: [],
        expandedRows: [],
        commentListData: null,
        commentListInitialData: null,
        commentTitle: "",
        commentTag: [],
        commentSummaryTag: [],
        commentText: { innerHTML: "", dropodownList: [] },
        commentSummary: { innerHTML: "", dropdownList: [] },
        showLoader: false,
        searchText: "",
        filterTagList: storeData?.clTagList,
        selectedFilterTagID: [],
        totalRowCount: -1,
        filterType: "all",
        listLoaded: false,
        globalSearchTableData: [],
        openAddDialog: false,
        commentDropdownList: [],
        summaryDropdownList: [],
        isEdit: false,
        refreshTs: null,
    });
    const isGlobalListEnable = globalListStoreData?.globalListEnable;

    const TAB_INDICES = {
        comments: 0,
        lists: isGlobalListEnable === 1 ? 1 : -1,
        tags: isGlobalListEnable === 1 ? 2 : 1,
        summaries: isGlobalListEnable === 1 ? 3 : 2,
    };

    useEffect(() => {
        if (searchParams.get("tab") === "ratings") {
            setTabValue(TAB_INDICES.summaries);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchParams, isGlobalListEnable]);

    const [debouncedSearch, setDebouncedSearch] = useState("");

    useEffect(() => {
        const t = setTimeout(() => {
            setDebouncedSearch((state.searchText || "").trim());
        }, 500);
        return () => clearTimeout(t);
    }, [state.searchText]);

    const GetCommentList = (formData, type) => {
        if (!storeData?.isHTCompany) return;
        setState((prevState) => ({ ...prevState, showLoader: true }));
        apiCall(
            (response) => {
                const { data, message, success, tagsList } = response?.data;
                if (success) {
                    setState((prevState) => ({ ...prevState, commentListData: data?.data, totalRowCount: data?.total || 0, search: false, listLoaded: true, commentListInitialData: data?.data, showLoader: false }));
                    if (type !== "filter") {
                        setState((prevState) => ({ ...prevState, filterTagList: tagsList }));
                    }
                } else {
                    console.log("else", message);
                }
            },
            formData,
            "GET_COMMENT_LIBRARY_LIST"
        );
    };
    const handleTabChange = (event, newTabValue) => {
        // if (newTabValue === 2 && !storeData?.isHTCompany) return;
        setTabValue(newTabValue);
        setState((prevState) => ({ ...prevState, selectedFilterTagID: [] }));
        if (newTabValue === TAB_INDICES.comments) {
            GetCommentList({ page: 1, rows_per_page: parseInt(sessionStorage.getItem("commentsRowPerPage"), 10) || 10 });
        }
    };

    useEffect(() => {
        if (!storeData?.isHTCompany) return;
        GetCommentList({
            page: state.page + 1,
            rows_per_page: state.rowsPerPage,
            ...(STATUS_MAP[state.filterType] && { status: STATUS_MAP[state.filterType] }),
            ...(debouncedSearch && { searchText: debouncedSearch }),
            filterByType: state.filterType,
            filterByTagId: state.selectedFilterTagID,
            sort_field: state.orderBy,
            sort_direction: state.order
        });
    }, [
        storeData?.isHTCompany,
        state.page,
        state.rowsPerPage,
        state.filterType,
        state.selectedFilterTagID,
        debouncedSearch,
        importing,
        state.refreshTs,
        state.order,
        state.orderBy
    ]);

     useEffect(() => {
            window.scrollTo(0, 0);
          }, []);
          
    return (
        <>
            <Box sx={{ flexGrow: 1 }} className="page-background">
                <Header />
                <Breadcrumb />
                <div className="library-tabs">
                    {importing && (
                        <ImportProgress
                            progress={progress}
                            label={tabValue === TAB_INDICES.lists ? "Importing lists" : "Importing comments"}
                        />
                    )}
                    <StyledTabs value={tabValue} className="styled-tab" onChange={handleTabChange} aria-label="styled tabs example">
                        <StyledTab label="Comments" width={matches ? "215px" : ""} />
                        {isGlobalListEnable === 1 && <StyledTab label="Lists" width={matches ? "215px" : ""} />}
                        {/* <StyledTab label="Media" width={matches ? "215px" : ""} /> */}
                        <StyledTab label="Tags" width={matches ? "215px" : ""} />
                        <StyledTab label={storeData?.isHTCompany ? "Summaries" : "Ratings"} width={matches ? "215px" : ""} />
                    </StyledTabs>
                    <Divider sx={{ border: "0px 0px 1px 0px !important", color: "#D5D5D5" }} />
                    {tabValue === TAB_INDICES.comments && <LibraryTable tabValue={tabValue} importing={importing} setImporting={setImporting} progress={progress} setProgress={setProgress} setState={setState} state={state} GetCommentList={GetCommentList} />}
                    {tabValue === TAB_INDICES.lists && isGlobalListEnable === 1 && <GlobalList state={state} setState={setState} importing={importing} setImporting={setImporting} progress={progress} setProgress={setProgress} />}
                    {tabValue === TAB_INDICES.tags && <CLTagsTable isHTCompany={storeData?.isHTCompany} />}
                    {tabValue === TAB_INDICES.summaries && (storeData?.isHTCompany ? <CLSummary /> : <CLRating isHTCompany={storeData?.isHTCompany} />)}
                    {/* {tabValue === 2 && (storeData?.isHTCompany ? <CLSummary /> : <CLRating isHTCompany={storeData?.isHTCompany} />)} */}
                </div>
            </Box>
        </>
    );
}

export default CommentLibrary;